<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { contractRoute, draftRoute } from '@/router/links';
import { groupVaultKeys, isEmptyVaultGroups, type VaultUidGroups } from '@/utils/vaultSelection';
import { useVaultStore, type VaultItem } from '@/stores/vaultStore';
import { useDownloadManagerStore } from '@/stores/downloadManagerStore';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import Breadcrumb, { type BreadcrumbItem } from '@/components/Breadcrumb.vue';
import FileVaultActions from '@/components/file_vault/FileVaultActions.vue';
import FileVaultSelectionBar from '@/components/file_vault/FileVaultSelectionBar.vue';
import FileVaultList from '@/components/file_vault/FileVaultList.vue';
import FileVaultCreateModal from '@/components/file_vault/FileVaultCreateModal.vue';
import type { WordApi } from '@/office/word/wordTypes';
import FileVaultUploadModal from '@/components/file_vault/FileVaultUploadModal.vue';
import VaultDropConfirmModal from '@/components/file_vault/VaultDropConfirmModal.vue';
import ContractFileCreateModal from '@/components/contractManagement/ContractFileCreateModal.vue';
import { useVaultUpload, type VaultUploadResult } from '@/composables/useVaultUpload';
import { describeDrop, type DroppedFile, type VaultFileDropTarget } from '@/utils/vaultFileDrop';
import FileVaultRenameModal from '@/components/file_vault/FileVaultRenameModal.vue';
import FileVaultMoveModal from '@/components/file_vault/FileVaultMoveModal.vue';
import FileVaultDeleteModal from '@/components/file_vault/FileVaultDeleteModal.vue';
import ReassignVaultOwnerModal from '@/components/file_vault/ReassignVaultOwnerModal.vue';
import VaultShareModal from '@/components/file_vault/VaultShareModal.vue';
import MassShareContractsModal from '@/components/contractManagement/MassShareContractsModal.vue';
import type { VaultShareTarget } from '@/services/vaultShareService';
import { ContractFileDownloadService } from '@/services/contractManagement/contractFileDownloadService';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaNotFound from '@/components/design/composite/GaiaNotFound.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { searchVault, VaultFileRevisionKind, VaultKind, type VaultFileInDb } from '@/api';
import { useDialog } from 'naive-ui';
import { IconHome } from '@/components/design/icons';
import { useAuthStore } from '@/stores/auth';
import { isOfficeAddinHost } from '@/office/utils';
import { isArchive } from '@/utils/fileTypeUtils';
import { ACCESS_PERMISSION_WRITE, deniedReason } from '@/utils/accessPermission';
import {
  DEFAULT_FILE_VAULT_COLUMNS,
  type FileVaultColumnKey,
} from '@/components/file_vault/fileVaultColumns';
import {
  DEFAULT_FILE_VAULT_ACTIONS,
  type FileVaultActionKey,
} from '@/components/file_vault/fileVaultActions';
import { logger } from '@/utils/logger';
import { isAccessDenied } from '@/utils/errorHandler';
import { askAiLimitWarning, askAiRoute } from '@/utils/askAi';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import { useGaiaToast, type GaiaToastOptions } from '@/composables/useGaiaToast';
import { navLabels } from '@/components/design/domain/navigation/navLabels';

const props = withDefaults(
  defineProps<{
    /**
     * When set, the vault runs embedded and rooted at this directory: navigation
     * is local state (no /vault routes) and can never go above the root.
     */
    rootDirectoryUid?: string;
    /** Breadcrumb label for the embedded root (defaults to the directory name). */
    rootLabel?: string;
    /**
     * Which of the organisation's vaults this browses. Drives the search scope and,
     * for the system vault, pins its top two levels and disables drag-and-drop (the
     * system vault accepts no incoming moves). Pair `system` with `rootDirectoryUid`.
     */
    vault?: VaultKind;
    /**
     * The vault's own folder trail. A page that shows the trail in its header
     * breadcrumb turns it off; modals and embedded browsers keep it.
     */
    showBreadcrumbs?: boolean;
    /**
     * Take files and folders dragged in from the OS: onto a folder's or a
     * contract's name, or onto the table for the current folder. Off by default;
     * the vault page turns it on, pickers and modals leave it off.
     */
    fileDrop?: boolean;
    /** Inline search (searches the whole vault, not just the current subtree). */
    showSearch?: boolean;
    /** Upload files / upload folder actions. */
    showUpload?: boolean;
    /** "New Folder" action. */
    showCreateFolder?: boolean;
    /** Icon-only toolbar buttons (with tooltips) instead of text labels, to save space. */
    topButtonIconMode?: boolean;
    /** Double-clicking a file opens the preview drawer (embedded) / versions view (main vault). */
    previewOnDblclick?: boolean;
    /**
     * Embedded only: start in this subdirectory instead of the root, with the
     * files whose current revision matches selected. Lets embedders that
     * persist vault UI state (see the directory-change / selection-change
     * emits) restore it across remounts.
     */
    initialDirectoryUid?: string;
    initialSelectedRevisionUids?: string[];
    /**
     * Available row actions. Each row keeps the actions that apply to it,
     * sorted by label with its type's trailing actions last; the last
     * `inlineActionCount` of those render as inline icon buttons, everything
     * before folds into the "⋯" menu.
     * Membership also gates the related bulk actions and modals.
     */
    actions?: FileVaultActionKey[];
    /** Per row, how many trailing applicable actions render as icon buttons. */
    inlineActionCount?: number;
    /** Visible listing columns, in display order. */
    columns?: FileVaultColumnKey[];
    /** Number of leading listing columns frozen (sticky) on the left. */
    frozenLeft?: number;
    /** Number of trailing listing columns frozen (sticky) on the right. */
    frozenRight?: number;
  }>(),
  {
    rootDirectoryUid: undefined,
    rootLabel: undefined,
    vault: VaultKind.MAIN,
    showBreadcrumbs: true,
    fileDrop: false,
    showSearch: true,
    showUpload: true,
    showCreateFolder: true,
    topButtonIconMode: false,
    previewOnDblclick: true,
    initialDirectoryUid: undefined,
    initialSelectedRevisionUids: undefined,
    actions: () => [...DEFAULT_FILE_VAULT_ACTIONS],
    inlineActionCount: 3,
    columns: () => [...DEFAULT_FILE_VAULT_COLUMNS],
    frozenLeft: 0,
    frozenRight: 5,
  }
);

const emit = defineEmits<{
  /** Revision uids of the selected files (files without a revision are omitted). */
  'selection-change': [revisionUids: string[]];
  'directory-change': [directoryUid: string];
}>();

const isEmbedded = computed(() => Boolean(props.rootDirectoryUid));
const isSystemVault = computed(() => props.vault === VaultKind.SYSTEM);

// Store access
const vaultStore = useVaultStore();
const downloadManagerStore = useDownloadManagerStore();
const authStore = useAuthStore();
const gaiaStore = useGaiaStore();
const integrationsStore = useIntegrationsStore();
const message = useGaiaToast();
const dialog = useDialog();
const router = useRouter();
const route = useRoute();

// Local state
const showCreateFolderModal = ref<boolean>(false);
const showUploadModal = ref<boolean>(false);
/** The contract row whose "Add a file" is open. */
const addFileContractUid = ref<string | null>(null);
const showAddFileModal = computed({
  get: () => addFileContractUid.value !== null,
  set: (show: boolean) => {
    if (!show) addFileContractUid.value = null;
  },
});
const showUploadFolderModal = ref<boolean>(false);
const autoLoadCurrentDocInModal = ref<boolean>(false);
const showRenameModal = ref<boolean>(false);
const showMoveModal = ref<boolean>(false);
const showDeleteModal = ref<boolean>(false);
const showReassignOwnerModal = ref<boolean>(false);
const showGaiaSectionModal = ref<boolean>(false);
const showPreviewDrawer = ref<boolean>(false);
const previewFileUid = ref<string | null>(null);
const selectedGaiaSections = ref<string[]>([]);
const isExportingToGaia = ref<boolean>(false);
const gaiaExportError = ref<string | null>(null);
const gaiaExportFileKeys = ref<string[]>([]);
const reassignOwnerMode = ref<'single' | 'bulk'>('single');
const reassignOwnerGroups = ref<VaultUidGroups>(groupVaultKeys([]));
const reassignOwnerAllowUnassign = ref<boolean>(false);
const reassignOwnerCurrentOwnerLabels = ref<string[]>([]);
const itemToRename = ref<VaultItem | null>(null);
const moveModalItems = ref<VaultItem[]>([]);
const currentRootDirectoryUid = ref<string | null>(null);
/** The backend refused the routed folder: deleted, no access, or another organisation's. */
const directoryUnavailable = ref(false);
const targetDirectoryUid = ref<string | null>(null);
const targetDirectoryNameValue = ref<string>('My Vault');
// Map to store directory names by their key for more reliable lookup
const directoryNameMap = ref<Map<string, string>>(new Map());

// Local breadcrumb state — same pattern as VaultFileBrowser picker
const breadcrumbs = ref<{ uid: string | null; label: string }[]>([
  { uid: null, label: 'My Vault' },
]);

// Inline search state — same pattern as VaultFileBrowser picker
const inlineSearchQuery = ref('');
const searchItems = ref<VaultItem[]>([]);
const isSearchLoading = ref(false);
const searchDebounceHandle = ref<number | null>(null);
const isSearchActive = computed(() => inlineSearchQuery.value.trim().length > 0);

// The system vault's top two levels (Templates / Intake Agent, then each
// template's / agent's folder) are pinned by the platform. Nothing flags them any
// more, so decide by location: listing the root (1 crumb) or a level-1 folder (2
// crumbs). Search results carry no depth, so every folder in them stays locked.
const lockDirectories = computed<boolean>(
  () => isSystemVault.value && (isSearchActive.value || breadcrumbs.value.length <= 2)
);

function mapSearchFileToItem(file: VaultFileInDb): VaultItem {
  return {
    key: `file-${file.uid}`,
    type: 'file',
    name: file.current_revision?.file_name ?? '',
    lastModified: file.updated_at ?? file.created_at,
    contentType: file.current_revision?.mime_type,
    size: file.current_revision?.size_b ?? undefined,
    previewStatus: file.current_revision?.preview_status,
    ocrStatus: file.current_revision?.ocr_status,
    conversionStatus: file.current_revision?.conversion_status,
    owner_user: file.owner_user
      ? {
          uid: file.owner_user.uid,
          full_name: file.owner_user.full_name,
          email: file.owner_user.email,
        }
      : null,
    revisionUid: file.current_revision?.uid,
    versionNumber: file.current_revision?.version_number,
    isDraft: file.current_revision?.kind === VaultFileRevisionKind.TEMPLATED,
    signatureInfos: file.current_revision?.signature_infos ?? null,
    converting: file.converting,
    permissions: file.permissions ?? null,
    path: [],
    // What the listing's type label would say for a plain file.
    typeLabel: isArchive(file.current_revision?.mime_type, file.current_revision?.file_name ?? '')
      ? 'archive'
      : 'file',
  };
}

/** Newest search wins: a slower earlier response must neither overwrite the
    results nor clear the loading flag while a later request is in flight. */
let searchSeq = 0;

async function performSearch(query: string): Promise<void> {
  const seq = ++searchSeq;
  isSearchLoading.value = true;
  try {
    const res = await searchVault({
      body: { text: query, limit: 50, offset: 0, vault: props.vault },
    });
    if (seq !== searchSeq) return;
    if (!res.data) return;
    // Grouped like a folder listing: folders, then contracts, then plain files.
    // A contract matches on its own fields or any of its files' names.
    const mapped: VaultItem[] = [];
    for (const dir of res.data.directory_results) {
      mapped.push({
        key: `dir-${dir.uid}`,
        type: 'directory',
        typeLabel: 'folder',
        name: dir.directory_name,
        lastModified: dir.updated_at ?? dir.created_at,
        permissions: dir.permissions ?? null,
        owner_user: dir.owner_user
          ? {
              uid: dir.owner_user.uid,
              full_name: dir.owner_user.full_name,
              email: dir.owner_user.email,
            }
          : null,
        path: [],
      });
    }
    for (const contract of res.data.contract_results) {
      mapped.push({
        key: `contract-${contract.uid}`,
        type: 'contract',
        name: contract.name,
        lastModified: contract.updated_at ?? contract.created_at,
        typeLabel: contract.contract_type?.name,
        contractFileCount: contract.contract_file_count,
        permissions: contract.permissions ?? null,
        owner_user: contract.owner_user
          ? {
              uid: contract.owner_user.uid,
              full_name: contract.owner_user.full_name,
              email: contract.owner_user.email,
            }
          : null,
        path: [],
      });
    }
    for (const file of res.data.file_results) {
      mapped.push(mapSearchFileToItem(file));
    }
    searchItems.value = mapped;
  } catch {
    if (seq === searchSeq) searchItems.value = [];
  } finally {
    if (seq === searchSeq) isSearchLoading.value = false;
  }
}

watch(inlineSearchQuery, value => {
  if (searchDebounceHandle.value) window.clearTimeout(searchDebounceHandle.value);
  if (!value.trim()) {
    // A cancelled pending search must not leave the skeletons up.
    searchSeq++;
    isSearchLoading.value = false;
    searchItems.value = [];
    return;
  }
  // Loading starts at the keystroke, not when the debounce fires — the list
  // swaps to (empty) search results immediately, and without this it renders
  // "no results" for the debounce window + request time before the first
  // response lands.
  isSearchLoading.value = true;
  searchDebounceHandle.value = window.setTimeout(() => void performSearch(value), 300);
});

// Entering/leaving search swaps the visible listing — drop the selection so
// the toolbar never acts on rows the table can no longer show.
watch(isSearchActive, () => {
  vaultStore.setSelectedItems([]);
});

// Computed state
const isLoading = computed<boolean>(() => vaultStore.isLoading);
const currentItems = computed(() => vaultStore.items);
const displayItems = computed(() =>
  isSearchActive.value ? searchItems.value : currentItems.value
);
const selectedItems = computed(() => vaultStore.selectedItems);
const hasSelectedItems = computed(() => vaultStore.hasSelectedItems);

// Sits beside the page title, the same way the CM list pages count their rows.
// A folder's total, not just the page on screen; search results are all here.
const countLabel = computed(() => {
  const count = isSearchActive.value ? displayItems.value.length : vaultStore.itemsCount;
  return `${count} ${count === 1 ? 'item' : 'items'}`;
});

// Watch the store rather than handleSelectionChange so internal clears
// (navigation, bulk actions) also reach the parent. Selected keys always
// belong to the visible listing, so their items resolve from displayItems.
watch(selectedItems, keys => {
  emit(
    'selection-change',
    keys
      .filter(key => key.startsWith('file-'))
      .flatMap(key => {
        const revisionUid = findItemRecursively(displayItems.value, key)?.revisionUid;
        return revisionUid ? [revisionUid] : [];
      })
  );
});

// Surface store errors as toasts instead of a layout-shifting alert block.
watch(
  () => vaultStore.errors.length,
  length => {
    if (length === 0) return;
    for (const error of vaultStore.errors) {
      message.error(error);
    }
    vaultStore.clearErrors();
  }
);

// Get display name for target directory
const targetDirectoryName = computed<string>(() => {
  return targetDirectoryNameValue.value;
});

const breadcrumbItems = computed((): BreadcrumbItem[] =>
  breadcrumbs.value.map((crumb, index) => ({
    label: crumb.label,
    icon: index === 0 ? IconHome : null,
    clickable: index < breadcrumbs.value.length - 1,
    onClick:
      index < breadcrumbs.value.length - 1
        ? (): void => {
            navigateToBreadcrumbByIndex(index);
          }
        : null,
  }))
);

const selectedFileCount = computed<number>(
  () => selectedItems.value.filter(k => k.startsWith('file-')).length
);
const selectedFolderCount = computed<number>(
  () => selectedItems.value.filter(k => k.startsWith('dir-')).length
);
const selectedContractCount = computed<number>(
  () => selectedItems.value.filter(k => k.startsWith('contract-')).length
);
const hasSelectionForReassign = computed<boolean>(() => selectedItems.value.length > 0);

// Only offer Ask AI when the chat can actually take the selection, the same
// test the row's own "⋯" menu applies: a folder has no uid to travel in, and
// an archive's text is never extracted. Mixing files with archives still
// counts — those files go, and `askAiAboutKeys` says what was skipped.
const canAskAiSelection = computed<boolean>(() => {
  if (selectedFolderCount.value > 0 || selectedContractCount.value > 0) return false;
  // A draft has no extracted text until it is saved, so the chat would read it as empty.
  if (selectedVaultItems.value.some(item => item.isDraft)) return false;
  return selectedItems.value.some(key => {
    if (!key.startsWith('file-')) return false;
    const item = resolveItemByKey(key);
    return !item || !isArchive(item.contentType, item.name);
  });
});
const gaiaSectionOptions = computed(() => {
  return gaiaStore.sectionGroups.data
    .map(group => ({
      type: 'group',
      label: group.title,
      key: group.id || group.title,
      children: group.sections
        .filter(section => section.id && section.title)
        .map(section => ({
          label: section.title,
          value: section.id,
        })),
    }))
    .filter(group => group.children.length > 0);
});
const gaiaExportFileCount = computed<number>(() => gaiaExportFileKeys.value.length);

// Helper to find an item recursively in the current items structure
function findItemRecursively(items: VaultItem[], key: string): VaultItem | null {
  // First check direct children
  for (const item of items) {
    // Compare keys directly instead of constructing keys from UIDs
    if (item.key === key) {
      return item;
    }
  }

  // If not found, recursively check directories
  for (const item of items) {
    if (item.type === 'directory' && item.children && item.children.length > 0) {
      const extractedKey = item.key; // Use key directly
      const found = findItemRecursively(item.children, key);
      if (found) {
        // When found in children, store this parent directory's name in the map too
        // This helps with navigation context
        if (found.type === 'directory') {
          directoryNameMap.value.set(extractedKey, item.name);
        }
        return found;
      }
    }
  }

  return null;
}

// Functions
function handleCreateFolder(): void {
  // Directly extract the directory UID from the selected item
  if (selectedItems.value.length === 1 && selectedItems.value[0].startsWith('dir-')) {
    // Use the selected directory as the target
    const selectedKey = selectedItems.value[0];
    targetDirectoryUid.value = selectedKey.replace('dir-', '');

    // Look up the directory name from our map
    if (directoryNameMap.value.has(selectedKey)) {
      targetDirectoryNameValue.value =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        directoryNameMap.value.get(selectedKey) || 'Unknown Directory';
    } else {
      // Fall back to other methods if not in the map
      const item = vaultStore.getItemByKey(selectedKey);
      if (item && item.type === 'directory') {
        targetDirectoryNameValue.value = item.name;
        // Store for future use
        directoryNameMap.value.set(selectedKey, item.name);
      } else {
        const foundItem = findItemRecursively(currentItems.value, selectedKey);
        if (foundItem) {
          targetDirectoryNameValue.value = foundItem.name;
          // Store for future use
          directoryNameMap.value.set(selectedKey, foundItem.name);
        } else {
          // Use directory UID as last resort
          targetDirectoryNameValue.value = `Folder in ${targetDirectoryUid.value}`;
        }
      }
    }
  } else {
    // Use the current root directory
    targetDirectoryUid.value = currentRootDirectoryUid.value;
    // Use the last directory in the path for the name
    targetDirectoryNameValue.value = breadcrumbs.value.at(-1)?.label ?? 'Home';
  }

  showCreateFolderModal.value = true;
}

function handleUpload(): void {
  // Directly extract the directory UID from the selected item
  if (selectedItems.value.length === 1 && selectedItems.value[0].startsWith('dir-')) {
    // Use the selected directory as the target
    const selectedKey = selectedItems.value[0];
    targetDirectoryUid.value = selectedKey.replace('dir-', '');

    // Look up the directory name from our map
    if (directoryNameMap.value.has(selectedKey)) {
      targetDirectoryNameValue.value =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        directoryNameMap.value.get(selectedKey) || 'Unknown Directory';
    } else {
      // Fall back to other methods if not in the map
      const item = vaultStore.getItemByKey(selectedKey);
      if (item && item.type === 'directory') {
        targetDirectoryNameValue.value = item.name;
        // Store for future use
        directoryNameMap.value.set(selectedKey, item.name);
      } else {
        const foundItem = findItemRecursively(currentItems.value, selectedKey);
        if (foundItem) {
          targetDirectoryNameValue.value = foundItem.name;
          // Store for future use
          directoryNameMap.value.set(selectedKey, foundItem.name);
        } else {
          // Use directory UID as last resort
          targetDirectoryNameValue.value = `Folder in ${targetDirectoryUid.value}`;
        }
      }
    }
  } else {
    // Use the current root directory
    targetDirectoryUid.value = currentRootDirectoryUid.value;
    // Use the last directory in the path for the name
    targetDirectoryNameValue.value = breadcrumbs.value.at(-1)?.label ?? 'Home';
  }

  showUploadModal.value = true;
}

function handleUploadCurrentDoc(): void {
  handleUpload();
  autoLoadCurrentDocInModal.value = true;
}

watch(showUploadModal, open => {
  if (!open) autoLoadCurrentDocInModal.value = false;
});

function handleUploadFolder(): void {
  // Similar to handleUpload, determine the target directory
  if (selectedItems.value.length === 1 && selectedItems.value[0].startsWith('dir-')) {
    // Use the selected directory as the target
    const selectedKey = selectedItems.value[0];
    targetDirectoryUid.value = selectedKey.replace('dir-', '');

    // Look up the directory name from our map
    if (directoryNameMap.value.has(selectedKey)) {
      targetDirectoryNameValue.value =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        directoryNameMap.value.get(selectedKey) || 'Unknown Directory';
    } else {
      // Fall back to other methods if not in the map
      const item = vaultStore.getItemByKey(selectedKey);
      if (item && item.type === 'directory') {
        targetDirectoryNameValue.value = item.name;
        // Store for future use
        directoryNameMap.value.set(selectedKey, item.name);
      } else {
        const foundItem = findItemRecursively(currentItems.value, selectedKey);
        if (foundItem) {
          targetDirectoryNameValue.value = foundItem.name;
          // Store for future use
          directoryNameMap.value.set(selectedKey, foundItem.name);
        } else {
          // Use directory UID as last resort
          targetDirectoryNameValue.value = `Folder in ${targetDirectoryUid.value}`;
        }
      }
    }
  } else {
    // Use the current root directory
    targetDirectoryUid.value = currentRootDirectoryUid.value;
    // Use the last directory in the path for the name
    targetDirectoryNameValue.value = breadcrumbs.value.at(-1)?.label ?? 'Home';
  }

  // Show the upload folder modal
  showUploadFolderModal.value = true;
}

function handleRename(item: VaultItem): void {
  itemToRename.value = item;
  showRenameModal.value = true;
}

function handleMove(item: VaultItem): void {
  moveModalItems.value = [item];
  showMoveModal.value = true;
}

// Resolve a selection key to its VaultItem — check the visible listing first
// (covers search results, which never enter the store), then the store.
function resolveItemByKey(key: string): VaultItem | null {
  return (
    displayItems.value.find(i => i.key === key) ??
    vaultStore.getItemByKey(key) ??
    findItemRecursively(currentItems.value, key)
  );
}

function resolveSelectedVaultItems(): VaultItem[] {
  return selectedItems.value
    .map(key => resolveItemByKey(key))
    .filter((item): item is VaultItem => item !== null);
}

/** Uploading or creating a folder here needs WRITE on the folder being shown. */
const createBlockedReason = computed<string | null>(() =>
  deniedReason(vaultStore.currentDirectory?.permissions, ACCESS_PERMISSION_WRITE)
);

// ── Drop-to-upload ──────────────────────────────────────────────────────────

/**
 * A drop onto the table (the folder being shown) up to this many files goes
 * straight in; a larger one asks first. A drop onto a folder's or contract's
 * name always asks.
 */
const DROP_CONFIRM_ABOVE = 10;

const { uploadTree, uploadToContract } = useVaultUpload();

/** Why a drop onto the table (into the folder being shown) is refused. */
const fileDropHereBlocked = computed<string | null>(() => {
  if (isSystemVault.value) return 'Nothing can be added to the system files';
  if (isSearchActive.value) return 'Clear the search to drop files into a folder';
  return createBlockedReason.value;
});

/** A drop waiting on the confirm modal. */
const pendingDrop = ref<{ target: VaultFileDropTarget; files: DroppedFile[] } | null>(null);

/** What a drop is called in the modal and the toasts. */
function dropTargetName(target: VaultFileDropTarget): string {
  return target.kind === 'here' ? (breadcrumbs.value.at(-1)?.label ?? 'this folder') : target.name;
}

const showDropConfirm = computed({
  get: () => pendingDrop.value !== null,
  set: (show: boolean) => {
    if (!show) pendingDrop.value = null;
  },
});

function handleFilesDropped(target: VaultFileDropTarget, files: DroppedFile[]): void {
  if (files.length === 0) {
    message.warning('Nothing to upload — the drop held no files');
    return;
  }
  if (target.kind !== 'here' || files.length > DROP_CONFIRM_ABOVE) {
    pendingDrop.value = { target, files };
    return;
  }
  void runDrop(target, files);
}

function confirmDrop(): void {
  const drop = pendingDrop.value;
  pendingDrop.value = null;
  if (drop) void runDrop(drop.target, drop.files);
}

async function runDrop(target: VaultFileDropTarget, files: DroppedFile[]): Promise<void> {
  const targetName = dropTargetName(target);
  const what = describeDrop(files);
  const toast = message.loading(`Uploading ${what} to “${targetName}”…`);

  let result: VaultUploadResult;
  try {
    if (target.kind === 'contract') {
      result = await uploadToContract(files, target.uid);
    } else {
      const parentUid =
        target.kind === 'directory' ? target.uid : (currentRootDirectoryUid.value ?? undefined);
      result = await uploadTree(files, parentUid);
    }
  } finally {
    toast.destroy();
  }

  if (result.failed.length === 0) {
    if (target.kind === 'contract') {
      message.success(`Added ${what} to “${targetName}” — being classified`);
    } else {
      message.success(`Uploaded ${what} to “${targetName}”`, viewInUploadHistory());
    }
  } else {
    logger.error('Drop upload failures:', result.failed);
    message.error(
      `${result.failed.length} of ${files.length} couldn’t be uploaded to “${targetName}”: ${result.failed[0]}`
    );
  }

  refreshCurrentDirectory();
}

/**
 * The upload toast's way on to the Upload History page, where each file's intake
 * shows. It sorts newest first, so the files just sent land on top. The Office
 * add-in has no such page in its toolbar, so its toasts don't offer it.
 */
function viewInUploadHistory(): GaiaToastOptions | undefined {
  if (isOfficeAddinHost()) return undefined;
  return {
    action: {
      label: `View in ${navLabels.uploadHistory}`,
      onClick: () => void router.push({ name: 'upload-history' }),
    },
  };
}

/** The upload modal closed on a full success — it has no toast of its own. */
function handleModalUploaded(complete: boolean): void {
  refreshCurrentDirectory();
  if (complete)
    message.success(`Uploaded to “${targetDirectoryName.value}”`, viewInUploadHistory());
}

/** Another page of the open folder; the selection belongs to the page it was made on. */
function handlePageChange({ page, pageSize }: { page: number; pageSize: number }): void {
  vaultStore.setSelectedItems([]);
  vaultStore.setListingPage(page, pageSize).catch((error: unknown) => {
    logger.error('Failed to load the page:', error);
    message.error('Failed to load the page');
  });
}

/** The selected rows this page can resolve, for the bar's permission checks. */
const selectedVaultItems = computed<VaultItem[]>(() =>
  selectedItems.value.flatMap(key => resolveItemByKey(key) ?? [])
);

const singleSelectedItem = computed<VaultItem | null>(() =>
  selectedItems.value.length === 1 ? resolveItemByKey(selectedItems.value[0]) : null
);

// Open the GAIA chat scoped to the given vault keys. Files only: the chat resolves a
// reference to a concrete file revision, so folders (and `gaia-` CLM sections) have no
// uid list to travel in and are dropped here rather than sent and silently ignored.
function askAiAboutKeys(keys: string[]): void {
  // Folders can't be referenced (no backend field), and archives can't be read (the
  // pipeline never extracts their text) — both drop out with one combined notice.
  const fileKeys = keys.filter(key => {
    if (!key.startsWith('file-')) return false;
    const item = resolveItemByKey(key);
    return !item || !isArchive(item.contentType, item.name);
  });
  const fileUids = fileKeys.map(key => key.slice(5));
  const droppedCount = keys.length - fileUids.length;

  if (fileUids.length === 0) {
    message.warning('Ask AI works on files — folders and archives can’t be attached.');
    return;
  }

  const limitWarning = askAiLimitWarning({ fileUids });
  if (limitWarning) {
    message.warning(limitWarning);
    return;
  }
  if (droppedCount > 0) {
    message.warning(
      `Asking about ${fileUids.length} file(s); folders and archives can't be read by the AI and were skipped.`
    );
  }

  void router.push(askAiRoute({ fileUids }));
}

function handleAskAiBulk(): void {
  askAiAboutKeys(selectedItems.value);
}

// ── Sharing ─────────────────────────────────────────────────────────────────

const shareTarget = ref<VaultShareTarget | null>(null);
const shareTargetName = ref('');
const showShareModal = ref(false);
const bulkShareGroups = ref<VaultUidGroups>(groupVaultKeys([]));
const showBulkShareModal = ref(false);

const SHARE_KIND: Record<VaultItem['type'], VaultShareTarget['kind']> = {
  file: 'file',
  contract: 'contract',
  directory: 'directory',
};

/** One item: who it is shared with, and sharing it further. */
function handleShare(item: VaultItem): void {
  const groups = groupVaultKeys([item.key]);
  shareTarget.value = {
    kind: SHARE_KIND[item.type],
    uid: [...groups.fileUids, ...groups.contractUids, ...groups.directoryUids][0],
  };
  shareTargetName.value = item.name;
  showShareModal.value = true;
}

/** The selection: one item opens its share dialog, several share in one go. */
function handleBulkShare(): void {
  const item = singleSelectedItem.value;
  if (item) {
    handleShare(item);
    return;
  }
  bulkShareGroups.value = groupVaultKeys(selectedItems.value);
  showBulkShareModal.value = true;
}

function handleBulkMove(): void {
  const items = resolveSelectedVaultItems();
  if (items.length === 0) return;
  moveModalItems.value = items;
  showMoveModal.value = true;
}

async function handleBulkDownload(): Promise<void> {
  const items = resolveSelectedVaultItems();
  if (items.length === 0) return;

  // Folders become independent zip jobs in the global DownloadsPanel; files
  // download sequentially to avoid holding many blobs in memory at once.
  // Both helpers report their own failures (toast / error alert).
  for (const item of items.filter(i => i.type === 'directory')) {
    await handleDownloadFolder(item);
  }
  for (const item of items.filter(i => i.type === 'file')) {
    await downloadFile(item);
  }
  for (const item of items.filter(i => i.type === 'contract')) {
    await downloadContract(item);
  }
}

/** A contract has no file of its own: each of its files downloads, one after another. */
async function downloadContract(item: VaultItem): Promise<void> {
  try {
    const files = await ContractFilesService.getAllContractFilesForContract(
      item.key.replace('contract-', '')
    );
    if (files.length === 0) {
      message.warning(`${item.name} has no files to download.`);
      return;
    }
    for (const file of files) {
      const result = await ContractFileDownloadService.downloadContractFile(file.uid);
      if (!result.success)
        message.error(result.error ?? `Failed to download a file of ${item.name}.`);
    }
  } catch (error) {
    logger.error('Contract download failed:', error);
    message.error(`Failed to download the files of ${item.name}.`);
  }
}

function handleSingleRename(): void {
  if (singleSelectedItem.value) handleRename(singleSelectedItem.value);
}

function handleSinglePreview(): void {
  const item = singleSelectedItem.value;
  if (item?.type === 'file') handlePreview(item);
}

function handleSingleVersions(): void {
  const item = singleSelectedItem.value;
  if (item?.type !== 'file') return;
  void router.push(`/vault/file/${item.key.replace('file-', '')}/versions`);
}

function handleSingleConvertToDocx(): void {
  const item = singleSelectedItem.value;
  if (item) void handleConvertToDocx(item);
}

function handleSingleConvertToContract(): void {
  const item = singleSelectedItem.value;
  if (item) void handleConvertToContract(item);
}

function handleSingleExportToGaia(): void {
  const item = singleSelectedItem.value;
  if (item) void openGaiaSectionModal([item.key]);
}

async function handleUnpack(item: VaultItem): Promise<void> {
  try {
    // Show loading state
    vaultStore.isLoading = true;

    const fileUid = item.key.replace('file-', '');
    await vaultStore.unpackFile(fileUid);

    // Show success message
    message.success(`Successfully unpacked ${item.name}`);

    // Refresh the current directory to show the unzipped contents
    refreshCurrentDirectory();
  } catch (error) {
    logger.error('Failed to unpack file:', error);
    // Show error message
    message.error(
      `Failed to unpack file: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  } finally {
    // Always reset loading state
    vaultStore.isLoading = false;
  }
}

async function handleDownloadFolder(item: VaultItem): Promise<void> {
  if (item.type !== 'directory') return;

  // The zip is built server-side as an async job; the global DownloadsPanel
  // shows progress and saves the file when it's ready.
  try {
    await downloadManagerStore.startDirectoryDownload(item.key.replace('dir-', ''));
  } catch (error) {
    logger.error('Failed to start folder download:', error);
    message.error(
      `Failed to start folder download: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

async function handleConvertToDocx(item: VaultItem): Promise<void> {
  if (item.type !== 'file') return;

  try {
    const fileUid = item.key.replace('file-', '');
    const result = await vaultStore.convertPdfToDocx(fileUid);

    message.success(result.message || `Started converting ${item.name} to DOCX`);
    refreshCurrentDirectory();
  } catch (error) {
    logger.error('Failed to convert file to DOCX:', error);
    message.error(
      `Failed to convert file to DOCX: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

function handleItemClick(item: VaultItem): void {
  if (item.type === 'file') {
    void downloadFile(item);
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = (): void => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to convert file to base64.'));
        return;
      }

      const commaIndex = result.indexOf(',');
      resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
    };
    reader.onerror = (): void => {
      reject(reader.error ?? new Error('Failed to read file.'));
    };
    reader.readAsDataURL(blob);
  });
}

function isCurrentWordDocumentEmpty(bodyText: string, bodyOoxml: string): boolean {
  if (bodyText.replace(/\s/g, '').length > 0) {
    return false;
  }

  const parsedDocument = new DOMParser().parseFromString(bodyOoxml, 'application/xml');
  if (parsedDocument.querySelector('parsererror')) {
    return bodyText.trim().length === 0;
  }

  const substantiveElementNames = new Set([
    'altChunk',
    'drawing',
    'object',
    'oMath',
    'pict',
    'sdt',
    'tbl',
  ]);

  return !Array.from(parsedDocument.querySelectorAll('*')).some(element => {
    const localName = element.localName;

    if (localName === 't' || localName === 'instrText') {
      return (element.textContent || '').replace(/\s/g, '').length > 0;
    }

    return substantiveElementNames.has(localName);
  });
}

async function insertVaultFileIntoCurrentWordDocument(item: VaultItem): Promise<void> {
  if (item.type !== 'file') {
    return;
  }

  const word = (window as Window & { Word?: WordApi }).Word;

  if (!word?.run) {
    message.error('Word document context is not available.');
    return;
  }

  const isDocx =
    item.contentType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    /\.docx$/i.test(item.name);

  if (!isDocx) {
    message.error('Only .docx files can be inserted into the current Word document.');
    return;
  }

  if (!item.revisionUid) {
    message.error('This file has no current revision and cannot be inserted into Word.');
    return;
  }

  try {
    vaultStore.isLoading = true;
    const blob = await vaultStore.downloadFile(
      item.revisionUid,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      item.contentType || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
    const base64File = await blobToBase64(blob);

    await word.run(async context => {
      const currentBody = context.document.body;
      currentBody.load('text');
      const currentBodyOoxml = currentBody.getOoxml();
      await context.sync();

      if (!isCurrentWordDocumentEmpty(currentBody.text, currentBodyOoxml.value)) {
        throw new Error(
          'The current Word document is not empty. Please open an empty document before inserting a vault file.'
        );
      }

      const selection = context.document.getSelection();
      const supportsHiddenDocument = Boolean(
        window.Office?.context?.requirements?.isSetSupported?.('WordApiHiddenDocument', '1.3')
      );

      if (supportsHiddenDocument && context.application?.createDocument) {
        const externalDoc = context.application.createDocument(base64File);
        await context.sync();

        const externalBody = externalDoc.body;
        const externalBodyOoxml = externalBody.getOoxml();
        await context.sync();

        selection.insertOoxml(externalBodyOoxml.value, 'Replace');
        await context.sync();
        return;
      }

      selection.insertFileFromBase64(base64File, 'Start');
      await context.sync();
    });

    message.success(`Inserted ${item.name} into the current Word document.`);
  } catch (error) {
    logger.error('Failed to insert file into current Word document:', error);
    const detailedMessage =
      error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : 'Failed to insert the selected file into the current Word document.';
    message.error(detailedMessage);
  } finally {
    vaultStore.isLoading = false;
  }
}

function handleInsertIntoWord(item: VaultItem): void {
  dialog.warning({
    title: 'Insert file into Word',
    content: 'This will insert the selected .docx file at the current cursor position in Word.',
    positiveText: 'Insert',
    negativeText: 'Cancel',
    onPositiveClick: () => insertVaultFileIntoCurrentWordDocument(item),
  });
}

function routeForDirectory(uid: string | null): string {
  return uid ? `/vault/directory/${uid}` : '/vault';
}

function handleItemDblClick(item: VaultItem): void {
  if (item.type === 'directory') {
    // Opening a folder leaves search mode; clearing here (not just in the
    // navigation watcher) also covers double-clicking the directory we're already in.
    inlineSearchQuery.value = '';
    const uid = item.key.replace('dir-', '');
    if (isEmbedded.value) {
      void navigateToDirectory(uid);
    } else {
      void router.push(routeForDirectory(uid));
    }
  } else if (item.type === 'contract') {
    void router.push(contractRoute(item.key.replace('contract-', '')));
  } else {
    if (!props.previewOnDblclick) {
      return;
    }
    // A draft opens back into the wizard, where it is edited.
    if (item.isDraft && !isEmbedded.value) {
      void router.push(draftRoute(item.key.replace('file-', '')));
      return;
    }
    if (isEmbedded.value) {
      handlePreview(item);
    } else {
      void router.push(`/vault/file/${item.key.replace('file-', '')}/versions`);
    }
  }
}

function navigateToBreadcrumbByIndex(index: number): void {
  if (isEmbedded.value) {
    const uid = breadcrumbs.value[index].uid;
    if (uid) void navigateToDirectory(uid);
  } else {
    void router.push(routeForDirectory(breadcrumbs.value[index].uid));
  }
}

function rebuildBreadcrumbsFromStore(): void {
  const path = vaultStore.directoryPath;
  if (isEmbedded.value) {
    // The store path runs all the way to the vault root; cut it off at the
    // embedded root so the user can never navigate above it.
    const rootUid = props.rootDirectoryUid as string;
    const rootIndex = path.findIndex(item => item.key === `dir-${rootUid}`);
    if (rootIndex === -1) {
      breadcrumbs.value = [{ uid: rootUid, label: props.rootLabel ?? 'Root' }];
      return;
    }
    breadcrumbs.value = path.slice(rootIndex).map((item, idx) => ({
      uid: item.key.replace('dir-', ''),
      label: idx === 0 ? (props.rootLabel ?? item.name) : item.name,
    }));
    return;
  }
  if (path.length === 0) {
    breadcrumbs.value = [{ uid: null, label: 'My Vault' }];
    return;
  }
  // A segment is the root crumb only by identity, never by position: on deep links
  // into shared folders the backend trims unreadable ancestors, so the trail may
  // start at a regular folder that must keep its own name and uid.
  breadcrumbs.value = path.map(item => {
    const uid = item.key.replace('dir-', '');
    const isRoot = uid === vaultStore.rootDirectoryUid;
    return {
      uid: isRoot ? null : uid,
      label: isRoot ? 'My Vault' : item.name,
    };
  });
}

// Embedded-mode counterpart of syncFromRoute: navigate via local state only.
// Revision uids resolve to row keys only once the directory items are loaded,
// so a restore keeps the previous selection untouched until then (clearing
// first would echo an empty selection-change to the embedder).
async function navigateToDirectory(uid: string, selectRevisionUids: string[] = []): Promise<void> {
  currentRootDirectoryUid.value = uid;
  if (selectRevisionUids.length === 0) {
    vaultStore.setSelectedItems([]);
  }
  emit('directory-change', uid);
  await vaultStore.navigateFlatToDirectory(uid);
  rebuildBreadcrumbsFromStore();
  if (selectRevisionUids.length > 0) {
    vaultStore.setSelectedItems(
      currentItems.value
        .filter(item => item.revisionUid && selectRevisionUids.includes(item.revisionUid))
        .map(item => item.key)
    );
  }
}

async function syncFromRoute(): Promise<void> {
  const paramUid = route.params.directoryUid;
  const uid = typeof paramUid === 'string' && paramUid.length > 0 ? paramUid : null;
  currentRootDirectoryUid.value = uid;
  vaultStore.setSelectedItems([]);
  directoryUnavailable.value = false;
  try {
    await vaultStore.navigateFlatToDirectory(uid);
  } catch (error) {
    if (isAccessDenied(error)) {
      // An answer, not a failure: the vault renders "not available" instead.
      logger.warn('Vault directory not available:', error);
      directoryUnavailable.value = true;
      return;
    }
    // The store already queued the message as a toast (see the errors watch).
    logger.error('Failed to load vault directory:', error);
    return;
  }
  rebuildBreadcrumbsFromStore();
}

async function downloadFile(item: VaultItem): Promise<void> {
  try {
    // Show loading state while downloading
    vaultStore.isLoading = true;

    const blob = await vaultStore.downloadFile(
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      item.revisionUid || item.key.replace('file-', ''),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      item.contentType || 'application/octet-stream'
    );

    // Use the item name directly, which should be correct now that we fixed the convertToVaultItems function
    const fileName = item.name;

    // Create a download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    logger.error('Failed to download file:', error);
    // Add error to the error store
    vaultStore.errors.push(
      `Failed to download file: ${error instanceof Error ? error.message : String(error)}`
    );
  } finally {
    // Always reset loading state
    vaultStore.isLoading = false;
  }
}

function handleSelectionChange(keys: string[]): void {
  // Log detailed selection information
  if (keys.length === 1 && keys[0].startsWith('dir-')) {
    const selectedKey = keys[0];
    const directoryUid = selectedKey.replace('dir-', '');

    // Try to find the item to verify it exists and store its name
    const item = vaultStore.getItemByKey(selectedKey);
    if (item && item.type === 'directory') {
      // Store the name in our map for future use
      directoryNameMap.value.set(selectedKey, item.name);
      targetDirectoryNameValue.value = item.name;
    } else {
      const foundItem = findItemRecursively(currentItems.value, selectedKey);
      if (foundItem) {
        // Store the name in our map for future use
        directoryNameMap.value.set(selectedKey, foundItem.name);
        targetDirectoryNameValue.value = foundItem.name;
      } else {
        logger.warn(`Cannot find selected directory with key: ${selectedKey}`);
        // Use a fallback name based on the ID
        targetDirectoryNameValue.value = `Directory ${directoryUid}`;
      }
    }

    // Log our current directory name map for debugging
  } else if (keys.length > 1) {
    // Reset to root directory name for multiple selections
    targetDirectoryNameValue.value = breadcrumbs.value.at(-1)?.label ?? 'Home';
  }

  // Update the vaultStore selection
  vaultStore.setSelectedItems(keys);
}

function refreshCurrentDirectory(): void {
  void vaultStore.navigateFlatToDirectory(currentRootDirectoryUid.value);
  vaultStore.setSelectedItems([]);
}

function handleFolderCreated(): void {
  refreshCurrentDirectory();
}

function handleItemMoved(
  item: VaultItem,
  targetDirectoryKey: string,
  currentDirectoryUid: string | null
): void {
  // Get the name of the target directory
  let targetName = 'the destination folder';
  if (directoryNameMap.value.has(targetDirectoryKey)) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    targetName = directoryNameMap.value.get(targetDirectoryKey) || targetName;
  } else if (vaultStore.directoryPath[0]?.key === targetDirectoryKey) {
    // The trail's first segment is only 'Home' when it really is the vault root;
    // on trimmed deep links it is a regular folder and keeps its own name.
    targetName = vaultStore.directoryPath[0].name;
  }

  // Show success message
  message.success(`Moved ${item.name} to ${targetName}`);

  // Keep the parent view state aligned with the directory the list just refreshed.
  currentRootDirectoryUid.value = currentDirectoryUid;
  vaultStore.setSelectedItems([]);
}

async function openGaiaSectionModal(fileKeys?: string[]): Promise<void> {
  const targetFileKeys = fileKeys ?? selectedItems.value.filter(key => key.startsWith('file-'));
  if (targetFileKeys.length === 0) {
    message.warning('Please select at least one file to add to a CLM section');
    return;
  }

  if (!integrationsStore.activeIntegrations.gaia) {
    message.error('GAIA integration is not available.');
    return;
  }

  gaiaExportFileKeys.value = targetFileKeys;
  selectedGaiaSections.value = [];
  gaiaExportError.value = null;
  showGaiaSectionModal.value = true;

  try {
    await gaiaStore.fetchSectionGroups('write');
  } catch (error) {
    logger.error('Failed to load Gaia sections:', error);
    gaiaExportError.value = 'Failed to load CLM sections. Please try again.';
  }
}

async function exportSelectedFilesToGaiaSections(): Promise<void> {
  if (gaiaExportFileKeys.value.length === 0 || selectedGaiaSections.value.length === 0) return;

  isExportingToGaia.value = true;
  gaiaExportError.value = null;

  try {
    await Promise.all(
      gaiaExportFileKeys.value.map(fileKey =>
        Promise.all(
          selectedGaiaSections.value.map(sectionId =>
            gaiaStore.exportVaultFileToGaia(fileKey.replace('file-', ''), sectionId)
          )
        )
      )
    );

    message.success(
      `Successfully exported ${gaiaExportFileKeys.value.length} file(s) to ${selectedGaiaSections.value.length} section(s)`
    );
    closeGaiaSectionModal();
  } catch (error) {
    logger.error('Failed to export files to Gaia:', error);
    gaiaExportError.value = 'Failed to export files to CLM sections. Please try again.';
    message.error('Failed to export files to CLM sections');
  } finally {
    isExportingToGaia.value = false;
  }
}

function closeGaiaSectionModal(): void {
  showGaiaSectionModal.value = false;
  selectedGaiaSections.value = [];
  gaiaExportError.value = null;
  gaiaExportFileKeys.value = [];
}

function getOwnerLabel(item: VaultItem | null): string {
  if (!item?.owner_user) return 'No owner';
  return item.owner_user.full_name || item.owner_user.email || 'Unknown';
}

function handlePreview(item: VaultItem): void {
  previewFileUid.value = item.key.replace('file-', '');
  showPreviewDrawer.value = true;
}

async function handleConvertToContract(item: VaultItem): Promise<void> {
  if (item.type !== 'file') return;
  try {
    await vaultStore.convertFileToContract(item.key.replace('file-', ''));
    message.success(`Converting ${item.name} into a contract…`);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to convert file to contract');
  }
}

function handleReassignOwnerFromList(item: VaultItem): void {
  reassignOwnerGroups.value = groupVaultKeys([item.key]);
  reassignOwnerCurrentOwnerLabels.value = item.owner_user
    ? [item.owner_user.full_name || item.owner_user.email || 'Unknown']
    : [];
  reassignOwnerMode.value = 'single';
  reassignOwnerAllowUnassign.value = authStore.userPermissions.is_org_admin;
  showReassignOwnerModal.value = true;
}

function handleReassignOwnerBulk(): void {
  const groups = groupVaultKeys(selectedItems.value);
  if (isEmptyVaultGroups(groups)) return;
  reassignOwnerGroups.value = groups;
  reassignOwnerMode.value = 'bulk';
  reassignOwnerAllowUnassign.value = authStore.userPermissions.is_org_admin;
  reassignOwnerCurrentOwnerLabels.value = selectedItems.value.map(key => {
    const item = findItemRecursively(currentItems.value, key);
    return getOwnerLabel(item ?? null);
  });
  showReassignOwnerModal.value = true;
}

// The very first embedded navigation may restore persisted state; every later
// one (root prop swapped to a different vault) starts clean at the new root.
let initialNavigationDone = false;
watch(
  () => (isEmbedded.value ? props.rootDirectoryUid : route.params.directoryUid),
  () => {
    // Any directory navigation (double-click, breadcrumbs, browser back/forward)
    // exits search mode so the listing shows the folder we navigated to.
    inlineSearchQuery.value = '';
    if (isEmbedded.value) {
      if (props.rootDirectoryUid) {
        const restore = !initialNavigationDone;
        initialNavigationDone = true;
        void navigateToDirectory(
          (restore ? props.initialDirectoryUid : undefined) ?? props.rootDirectoryUid,
          restore ? (props.initialSelectedRevisionUids ?? []) : []
        );
      }
    } else {
      void syncFromRoute();
    }
  },
  { immediate: true }
);

// --- Live vault updates over the websocket ---

useVaultWebSocket({
  // The breadcrumb chain (root → current) holds the real uids to watch.
  directoryUids: () => vaultStore.directoryPath.map(item => item.key.replace('dir-', '')),
  currentDirectoryUid: () => vaultStore.currentDirectoryUid,
  // While searching we watch the files in the result set; otherwise the
  // directory_updated poke keeps the listing fresh.
  fileUids: () =>
    isSearchActive.value
      ? searchItems.value
          .filter(item => item.type === 'file')
          .map(item => item.key.replace('file-', ''))
      : [],
  onCurrentDirectoryUpdated: () => {
    void vaultStore.getVaultDirectory(vaultStore.currentDirectoryUid ?? undefined);
  },
  onParentDirectoryUpdated: (uid, name) => {
    const crumb = breadcrumbs.value.find(entry => entry.uid === uid);
    if (crumb) {
      crumb.label = name;
    }
  },
  onCurrentOrParentDeleted: () => {
    if (isEmbedded.value) {
      void navigateToDirectory(props.rootDirectoryUid as string);
    } else {
      void router.push('/vault');
    }
  },
  onFileUpdated: (uid, fileData) => {
    const index = searchItems.value.findIndex(item => item.key === `file-${uid}`);
    if (index !== -1) {
      searchItems.value.splice(index, 1, mapSearchFileToItem(fileData as VaultFileInDb));
    }
  },
  onFileDeleted: uid => {
    searchItems.value = searchItems.value.filter(item => item.key !== `file-${uid}`);
  },
});
</script>

<template>
  <!-- Embedded hosts (e.g. a review project pane) size the vault themselves. -->
  <div
    class="file-vault"
    :class="{
      'file-vault--office': isOfficeAddinHost(),
      'file-vault--hosted': $slots.title && !isOfficeAddinHost(),
    }"
    :style="isEmbedded ? 'height: 100%; padding: 8px 0 0' : undefined"
  >
    <!-- The folder is gone, or not visible from the active organisation. -->
    <gaia-not-found v-if="directoryUnavailable" />
    <div
      v-else
      class="header-container"
      :class="{ 'header-container--office': isOfficeAddinHost() }"
    >
      <!-- Office add-in keeps its compact toolbar layout unchanged. The task
           pane is ~320px wide, so the title cannot share a row with it — the
           host renders it above instead, and `height: calc(100vh - 163px)`
           below is measured against that. -->
      <template v-if="isOfficeAddinHost()">
        <div class="vault-toolbar vault-toolbar--office">
          <div class="vault-toolbar-left">
            <template v-if="isSearchActive">
              <span class="search-results-label">Search results</span>
            </template>
            <template v-else>
              <Breadcrumb :items="breadcrumbItems" :max-visible-items="3" />
            </template>
          </div>
          <gaia-search-input
            v-if="showSearch"
            v-model:value="inlineSearchQuery"
            variant="panel"
            size="small"
            placeholder="Search contracts and files"
            class="vault-search-input vault-search-input--office"
          />
        </div>

        <file-vault-actions
          compact
          :icon-only="topButtonIconMode"
          :show-upload="showUpload"
          :show-create-folder="showCreateFolder"
          :create-blocked-reason="createBlockedReason"
          :show-delete="actions.includes('delete')"
          :show-reassign-owner="actions.includes('change-owner')"
          :has-selected-items="hasSelectedItems"
          :selected-count="selectedItems.length"
          :selected-file-count="selectedFileCount"
          :selected-folder-count="selectedFolderCount"
          :selected-contract-count="selectedContractCount"
          :has-selection-for-reassign="hasSelectionForReassign"
          :is-org-admin="authStore.userPermissions.is_org_admin"
          @create-folder="handleCreateFolder"
          @upload="handleUpload"
          @upload-folder="handleUploadFolder"
          @upload-current-doc="handleUploadCurrentDoc"
          @bulk-delete="showDeleteModal = true"
          @reassign-owner="handleReassignOwnerBulk"
        />
      </template>

      <!-- Standard layout: the toolbar row, then the breadcrumb trail below it
           once there is one, then the selection bar. Same geometry as the CM
           list pages — a single 36px control row, then the table. -->
      <template v-else>
        <div class="vault-actions-row" :class="{ 'has-title': $slots.title }">
          <!-- The page title rides this row when the route supplies one, so
               the vault's header costs exactly what a CM list header costs. -->
          <n-flex v-if="$slots.title" align="baseline" :size="10" class="vault-title">
            <slot name="title" />
            <gaia-count-label v-if="!isLoading">{{ countLabel }}</gaia-count-label>
          </n-flex>
          <span v-else-if="isSearchActive" class="search-results-label">Search results</span>
          <div v-else class="vault-breadcrumbs" data-tour="vault.breadcrumbs">
            <breadcrumb :items="breadcrumbItems" variant="chips" :max-visible-items="5" />
          </div>
          <!-- Explicit `size` wins over the hero variant's `large`: medium (36px)
               is the step the CM list pages put their search and buttons on, so
               the row is the same height and the table lands in the same place. -->
          <gaia-search-input
            v-if="showSearch"
            v-model:value="inlineSearchQuery"
            variant="hero"
            size="medium"
            class="row-raised"
            placeholder="Search contracts and files"
            data-tour="vault.search"
          />
          <!-- The host page's own buttons, just left of "Upload". -->
          <div v-if="$slots.actions" class="row-raised">
            <slot name="actions" />
          </div>
          <file-vault-actions
            class="row-raised"
            :icon-only="topButtonIconMode"
            :show-upload="showUpload"
            :show-create-folder="showCreateFolder"
            :create-blocked-reason="createBlockedReason"
            :has-selected-items="hasSelectedItems"
            :selected-count="selectedItems.length"
            @create-folder="handleCreateFolder"
            @upload="handleUpload"
            @upload-folder="handleUploadFolder"
          />
        </div>

        <!-- Only once the trail says something the title above does not: at the
             root it would just repeat "My Vault", so it costs a row for nothing. -->
        <div
          v-if="
            $slots.title &&
            (isSearchActive || (props.showBreadcrumbs && breadcrumbItems.length > 1))
          "
          class="vault-crumb-row"
        >
          <span v-if="isSearchActive" class="search-results-label">Search results</span>
          <div v-else class="vault-breadcrumbs" data-tour="vault.breadcrumbs">
            <breadcrumb :items="breadcrumbItems" variant="chips" :max-visible-items="5" />
          </div>
        </div>

        <!-- The slot is always rendered and reserves the bar's height, so the
             table doesn't jump down when the first row is ticked. Only the bar
             inside is conditional — and deliberately NOT wrapped in a
             <transition>: an animated leave gives the node an async removal
             window, and twice now (interrupted leaves, HMR swaps mid-leave)
             that window orphaned a stale bar that then stacked under the next
             one. A plain v-if removes it synchronously; nothing can pile up. -->
        <div class="vault-selection-slot">
          <file-vault-selection-bar
            v-if="hasSelectedItems"
            :actions="actions"
            :selected-count="selectedItems.length"
            :selected-file-count="selectedFileCount"
            :selected-folder-count="selectedFolderCount"
            :selected-contract-count="selectedContractCount"
            :is-org-admin="authStore.userPermissions.is_org_admin"
            :has-selection-for-reassign="hasSelectionForReassign"
            :can-ask-ai="canAskAiSelection"
            :single-item="singleSelectedItem"
            :selected-items="selectedVaultItems"
            :can-convert-to-contract="!isSystemVault && !singleSelectedItem?.isDraft"
            :can-share="!isSystemVault"
            :has-gaia-integration="integrationsStore.activeIntegrations.gaia"
            @clear-selection="vaultStore.setSelectedItems([])"
            @download="handleBulkDownload"
            @move="handleBulkMove"
            @share="handleBulkShare"
            @ask-ai="handleAskAiBulk"
            @reassign-owner="handleReassignOwnerBulk"
            @delete="showDeleteModal = true"
            @rename="handleSingleRename"
            @preview="handleSinglePreview"
            @versions="handleSingleVersions"
            @convert-to-docx="handleSingleConvertToDocx"
            @convert-to-contract="handleSingleConvertToContract"
            @export-to-gaia="handleSingleExportToGaia"
          />
        </div>
      </template>
    </div>
    <!-- File and directory listing with tree view -->
    <FileVaultList
      v-if="!directoryUnavailable"
      data-tour="vault.list"
      :actions="actions"
      :inline-action-count="inlineActionCount"
      :columns="columns"
      :frozen-left="frozenLeft"
      :frozen-right="frozenRight"
      :items="displayItems"
      :loading="isLoading || isSearchLoading"
      :selected-keys="selectedItems"
      :has-gaia-integration="integrationsStore.activeIntegrations.gaia"
      :can-convert-to-contract="!isSystemVault"
      :can-share="!isSystemVault"
      :is-search-results="isSearchActive"
      :lock-directories="lockDirectories"
      :disable-drag="isSystemVault"
      :file-drop="fileDrop"
      :file-drop-here-blocked="fileDropHereBlocked"
      :pagination="
        isSearchActive
          ? null
          : {
              page: vaultStore.listingPage.page,
              pageSize: vaultStore.listingPage.pageSize,
              total: vaultStore.itemsCount,
            }
      "
      @update:pagination="handlePageChange"
      @files-dropped="handleFilesDropped"
      @selection-change="handleSelectionChange"
      @item-click="handleItemClick"
      @item-dblclick="handleItemDblClick"
      @rename="handleRename"
      @move="handleMove"
      @reassign-owner="handleReassignOwnerFromList"
      @share="handleShare"
      @insert-into-word="handleInsertIntoWord"
      @item-moved="handleItemMoved"
      @download-folder="handleDownloadFolder"
      @unpack="handleUnpack"
      @convert-to-docx="handleConvertToDocx"
      @preview="handlePreview"
      @export-to-gaia="item => openGaiaSectionModal([item.key])"
      @convert-to-contract="handleConvertToContract"
      @add-file="item => (addFileContractUid = item.key.replace('contract-', ''))"
      @ask-ai="item => askAiAboutKeys([item.key])"
    />

    <!-- Modals -->
    <ContractFileCreateModal
      v-if="addFileContractUid"
      v-model:show="showAddFileModal"
      :contract-uid="addFileContractUid"
      @created="refreshCurrentDirectory"
    />

    <VaultDropConfirmModal
      v-if="pendingDrop"
      v-model:show="showDropConfirm"
      :target-name="dropTargetName(pendingDrop.target)"
      :target-kind="pendingDrop.target.kind === 'contract' ? 'contract' : 'directory'"
      :file-count="pendingDrop.files.length"
      @confirm="confirmDrop"
    />

    <FileVaultCreateModal
      v-if="showCreateFolder"
      v-model:show="showCreateFolderModal"
      :parent-directory-uid="targetDirectoryUid"
      :parent-directory-name="targetDirectoryName"
      @created="handleFolderCreated"
    />

    <FileVaultUploadModal
      v-if="showUpload"
      v-model:show="showUploadModal"
      :parent-directory-uid="targetDirectoryUid"
      :parent-directory-name="targetDirectoryName"
      :auto-load-current-doc="autoLoadCurrentDocInModal"
      @uploaded="handleModalUploaded"
    />

    <FileVaultUploadModal
      v-if="showUpload"
      v-model:show="showUploadFolderModal"
      :parent-directory-uid="targetDirectoryUid"
      :parent-directory-name="targetDirectoryName"
      :is-folder-upload="true"
      @uploaded="handleModalUploaded"
    />

    <FileVaultRenameModal
      v-model:show="showRenameModal"
      :item="itemToRename"
      @renamed="refreshCurrentDirectory"
    />

    <file-vault-move-modal
      v-if="actions.includes('move')"
      v-model:show="showMoveModal"
      :items="moveModalItems"
      :current-parent-uid="isSearchActive ? null : vaultStore.currentDirectoryUid"
      @moved="refreshCurrentDirectory"
    />

    <FileVaultDeleteModal
      v-if="actions.includes('delete')"
      v-model:show="showDeleteModal"
      :selected-items="selectedItems"
      @deleted="refreshCurrentDirectory"
    />

    <vault-share-modal
      v-model:show="showShareModal"
      :target="shareTarget"
      :name="shareTargetName"
    />

    <mass-share-contracts-modal
      v-model:show="showBulkShareModal"
      kind="vault"
      :vault-groups="bulkShareGroups"
    />

    <ReassignVaultOwnerModal
      v-if="actions.includes('change-owner')"
      v-model:show="showReassignOwnerModal"
      :mode="reassignOwnerMode"
      :groups="reassignOwnerGroups"
      :allow-unassign="reassignOwnerAllowUnassign"
      :current-owner-labels="reassignOwnerCurrentOwnerLabels"
      @reassigned="refreshCurrentDirectory"
    />

    <NModal
      v-model:show="showGaiaSectionModal"
      preset="card"
      style="width: 500px"
      title="Add Files to Gaia Section"
      @after-leave="closeGaiaSectionModal"
    >
      <div class="gaia-section-modal-content">
        <p>Select the Gaia sections where you want to export the selected files:</p>

        <gaia-select
          v-model:value="selectedGaiaSections"
          :options="gaiaSectionOptions"
          placeholder="Select sections..."
          multiple
          filterable
          :loading="gaiaStore.sectionGroups.loading"
          style="margin-bottom: 16px"
        />

        <div v-if="gaiaExportError" class="gaia-export-error">
          {{ gaiaExportError }}
        </div>

        <div v-if="selectedGaiaSections.length > 0" class="selected-sections-info">
          <p>
            <strong>{{ gaiaExportFileCount }}</strong> file(s) will be exported to
            <strong>{{ selectedGaiaSections.length }}</strong> section(s)
          </p>
        </div>
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="closeGaiaSectionModal">Cancel</NButton>
          <NButton
            type="primary"
            :loading="isExportingToGaia"
            :disabled="selectedGaiaSections.length === 0"
            @click="exportSelectedFilesToGaiaSections"
          >
            Export Files
          </NButton>
        </NSpace>
      </template>
    </NModal>

    <!-- Flush: the preview sizes itself to the drawer body and scrolls its own
         pages. Under the styled scrollbar the body grows to fit the content
         instead, so the viewer never scrolls — its page tracking then measures
         a viewport as tall as the whole document and opens long files on the
         wrong page with blank pages above. -->
    <gaia-drawer
      v-model:show="showPreviewDrawer"
      :width="640"
      title="File preview"
      flush
      @after-leave="previewFileUid = null"
    >
      <gaia-vault-file-pdf-preview v-if="previewFileUid" :vault-file-uid="previewFileUid" />
    </gaia-drawer>
  </div>
</template>

<style scoped>
.file-vault {
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* A host that supplies the page title also supplies the page gutters, so the
   vault must not add a second set inside them. */
.file-vault--hosted {
  padding: 0;
}

.file-vault--office {
  padding: 6px 0 0;
  /* Office host: no topbar, but a fixed bottom tab bar + tighter chrome. */
  height: calc(100vh - 163px);
}

/* 18px below the last header row, matching the gap the CM list pages put
   between their title row and their table. */
.header-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
  flex: 0 0 auto;
}

.header-container--office {
  gap: 8px;
  margin-bottom: 10px;
}

/* Single toolbar row: breadcrumbs | search | "+ New" (standard layout) */
.vault-actions-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.vault-breadcrumbs {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* Takes the row's slack so search and "+ New" are pushed to the right edge —
   the job .vault-breadcrumbs does on the row when there is no title. */
.vault-title {
  flex: 1;
  min-width: 0;
}

.vault-actions-row .search-results-label {
  flex: 1;
}

/* Lift search + "+ New" slightly so their bottoms sit on the breadcrumb
   text line (the breadcrumb's line box extends a few px below the text). */
.vault-actions-row .row-raised {
  margin-bottom: 4px;
}

/* With the page title on the row instead of the breadcrumb, there is no
   descending line box to compensate for — the row centres as it is. */
.vault-actions-row.has-title .row-raised {
  margin-bottom: 0;
}

/* The trail's own row, below the title. */
.vault-crumb-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* Reserves the selection bar's 42px whether or not it is shown, so ticking
   the first row swaps the bar into waiting space instead of pushing the
   table down. */
.vault-selection-slot {
  min-height: 42px;
}

.vault-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;
}

.vault-toolbar-left {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.vault-toolbar--office {
  gap: 6px;
  align-items: center;
}

.search-results-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  padding: 4px 0;
}

/* Two classes, so these beat the panel variant's own width rule inside the
   wrapper regardless of which stylesheet is injected first. */
.vault-toolbar .vault-search-input {
  flex: 0 1 240px;
  width: 240px;
}

.vault-toolbar .vault-search-input--office {
  flex-basis: 128px;
  width: 128px;
}

@media (max-width: 760px) {
  .vault-toolbar--office {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .vault-toolbar--office .vault-toolbar-left {
    flex: 0 0 auto;
  }

  .vault-toolbar .vault-search-input--office {
    flex: 0 0 auto;
    width: 100%;
  }
}
</style>
